import { Color } from './Color';
import { MoveType } from './MoveType';

// Mirrors GameCorrectionsModel / CorrectionMoveModel on the server (saveyourchess.Models), PascalCase.
export type GameCorrections = {
  GameId: number;
  Score: number; // 0..100, weighted; 100 = every mistake corrected (or none to correct)
  TotalWeight: number;
  SolvedWeight: number;
  Mistakes: CorrectionMove[];
};

export type CorrectionMove = {
  MoveId: number;
  GameId: number;
  Number: number;
  Color: Color;
  Fen: string; // position before the mistake, solved from
  Solution: string; // engine best move at Fen, UCI
  SideToMove: 'white' | 'black';
  PlayedMove: string; // SAN of the mistake played
  MoveType: MoveType;
  PlayedSwing: number; // win-chance points the played move gave up (≥0), for the "↓N%" reveal swing
  Weight: number; // inaccuracy 1 · mistake 2 · miss 3 · blunder 4
  Evaluation: number | null;
  // How much room the position being solved gave, for the good-move gauge. Null on positions evaluated
  // before the difficulty pass.
  GoodMoveCount?: number | null;
  LegalMoveCount?: number | null;
  Pv: string | null; // engine best line from Fen, space-separated UCI (starts with Solution)
  Commentary: string | null;
  // The mistake move's PGN path, the key notes are anchored to.
  MovePgn?: string | null;
  // Tier-0 theme tag (kebab-case), or null. Makes mistakes comparable across games.
  CommentaryTheme?: string | null;
  Solved: boolean;
  // Times the viewer has repaired this mistake, ever. Solved is `RepairCount > 0`.
  RepairCount: number;
  // The repair count the payout is priced on: RepairCount less the rungs rest has bought back. Price
  // previews off this, not RepairCount.
  PayoutPriorSuccesses: number;
  // What repairing it now pays in cents, without a hint. The server orders the queue by it.
  PayoutCents: number;
};

// Mirrors CorrectionAttemptResultModel: the counters after recording one attempt.
export type CorrectionAttemptResult = {
  MoveId: number;
  Correct: boolean;
  Attempts: number;
  Failures: number;
  Successes: number;
  Streak: number;
  NextReviewAt: string | null; // ISO; null until repaired at least once
  // Fake money in cents after this attempt. BalanceCents is the authoritative wallet; DeltaCents is
  // what moved (clamped at zero); FullAmountCents is the value before the clamp.
  BalanceCents: number;
  DeltaCents: number;
  FullAmountCents: number;
};

// Mirrors RepairQueueItemModel: one mistake in a cross-game session, with its history and game.
export type RepairQueueItem = CorrectionMove & {
  // RepairCount comes from CorrectionMove.
  Streak: number; // consecutive repairs; a failure resets it
  LastRepairedAt: string | null;
  NextReviewAt: string | null; // when spaced repetition wants it back; null = never repaired
  IsDue: boolean;
  GameWhite: string | null;
  GameBlack: string | null;
  GameDate: string;
  GameMoveTotal: number;
  GameTimeControl: string | null;
  ViewerColor: 'white' | 'black';
  History: string[]; // FENs, oldest first, ending on Fen (the position to solve)
  // How many mistakes in the whole session share this theme, and across how many games, counted before
  // the queue limit. 0 without a theme; the panel only mentions it from 2 up.
  ThemeOccurrences: number;
  ThemeGameCount: number;
};

// Mirrors RepairQueueModel, already in spaced-repetition order.
export type RepairQueue = {
  Items: RepairQueueItem[];
  DueCount: number;
  TotalCandidates: number;
  GamesConsidered: number;
};

// Session depths by mistake weight (CorrectionsService.MistakeWeight: blunder 4 · miss 3 · mistake 2
// · inaccuracy 1). `moveType` is the severity each depth adds, whose icon marks its row.
export const REPAIR_DEPTHS = [
  { minWeight: 4, label: 'Blunders', moveType: MoveType.Blunder },
  { minWeight: 3, label: 'Blunders & misses', moveType: MoveType.Miss },
  { minWeight: 2, label: '+ mistakes', moveType: MoveType.Mistake },
  { minWeight: 1, label: '+ inaccuracies', moveType: MoveType.Inaccuracy },
] as const;

// Mirrors EarningRowModel. `MoveType` is the enum NAME, since the header wallet is plain JS.
export type EarningRow = {
  Id: number;
  CreatedAt: string;
  AmountCents: number;
  AppliedCents: number | null;
  Success: boolean;
  SwingPoints: number;
  PriorSuccesses: number;
  GameId: number;
  Opponent: string | null;
  MoveNumber: number | null;
  San: string | null;
  MoveType: string | null;
  MoveRef: string | null; // "31w", deep-links the row to the move
};

// Mirrors RecentEarningsModel: what the wallet popover shows.
export type RecentEarnings = {
  Authenticated: boolean;
  BalanceCents: number;
  Items: EarningRow[];
  HasMore: boolean;
};

// Mirrors HomeSampleModel: a mistake plus the game it came from, for the trainer.
export type HomeSample = {
  Item: RepairQueueItem;
  Player: string | null;
  Rating: number | null;
  White: string | null;
  Black: string | null;
  WhiteRating: number | null;
  BlackRating: number | null;
  Date: string;
  Platform: string | null;
  Link: string | null;
};

// Mirrors GameFixModel: one game's mistakes for the trainer, oldest first.
export type GameFix = {
  GameId: number;
  Evaluated: boolean;
  Ready: boolean;
  White: string | null;
  Black: string | null;
  WhiteRating: number | null;
  BlackRating: number | null;
  Date: string;
  Platform: string | null;
  Link: string | null;
  TimeControl: string | null;
  Samples: HomeSample[];
};
